import { useState } from 'react';
import { PDFDocument } from 'pdf-lib';

import { logger } from '../utils/logger';

import type { ImageData } from '../types/pdf.types';

/**
 * Default behavior for form field flattening.
 * false = preserve editable fields (default)
 * true = flatten to non-editable content
 */
const DEFAULT_FLATTEN_BEHAVIOR = false;

interface UsePDFExportProps {
  pdfUrl: string;
  formData: Map<string, string | boolean | string[]>;
  fieldNameMapping: Map<string, string>; // Maps field.id -> field.name
  fieldTypeMapping: Map<string, string>; // Maps field.id -> field.type
  images: ImageData[];
  fieldNoExportMapping?: Map<string, boolean>; // Maps field.id -> noExport
  /**
   * Whether to flatten form fields (make non-editable) in the exported PDF.
   * When true, form fields are converted to static content.
   * When false (default), form fields remain editable.
   * @default false
   */
  flatten?: boolean;
}

interface UsePDFExportReturn {
  exportPDF: () => Promise<Uint8Array>;
  isExporting: boolean;
  error: string | null;
}

/**
 * Custom hook for exporting PDF with filled form data and embedded images
 * Uses pdf-lib to load, modify, and save PDF documents
 *
 * TODO: Add automated test coverage for flatten functionality.
 * Current testing: Manual browser testing only (see tech-spec).
 * Recommended: Add integration tests for both flatten=true and flatten=false paths.
 */
export const usePDFExport = ({
  pdfUrl,
  formData,
  fieldNameMapping,
  fieldTypeMapping,
  images,
  fieldNoExportMapping,
  flatten = DEFAULT_FLATTEN_BEHAVIOR,
}: UsePDFExportProps): UsePDFExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shouldFlatten =
    typeof flatten === 'boolean' ? flatten : DEFAULT_FLATTEN_BEHAVIOR;

  /**
   * Convert screen coordinates to PDF coordinates
   * Images are stored at unscaled (base) coordinates
   * Screen: top-left origin, y increases downward
   * PDF: bottom-left origin, y increases upward
   */
  const screenToPDFCoords = (
    x: number,
    y: number,
    pageHeight: number,
    imageHeight: number,
  ): { x: number; y: number } => {
    return {
      x,
      y: pageHeight - y - imageHeight, // Flip Y axis
    };
  };

  const exportPDF = async () => {
    setIsExporting(true);
    setError(null);
    logger.info('[usePDFExport] Export started');

    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      const arrayBuffer = await response.arrayBuffer();

      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const form = pdfDoc.getForm();

      formData.forEach((value, fieldId) => {
        try {
          if (fieldNoExportMapping?.get(fieldId) === true) {
            logger.warn(`[usePDFExport] Skipping noExport field ${fieldId}`);
            return;
          }

          const fieldName = fieldNameMapping.get(fieldId);
          const fieldType = fieldTypeMapping.get(fieldId);

          if (!fieldName) {
            logger.warn(
              `[usePDFExport] No field name mapping found for ${fieldId}`,
            );
            return;
          }

          if (fieldType === 'text' || fieldType === 'textarea') {
            const textField = form.getTextField(fieldName);
            if (Array.isArray(value)) {
              textField.setText(value.join(', '));
            } else {
              textField.setText(value as string);
            }
          } else if (fieldType === 'checkbox') {
            const checkBox = form.getCheckBox(fieldName);
            if (value) {
              checkBox.check();
            } else {
              checkBox.uncheck();
            }
          } else if (fieldType === 'radio') {
            if (value) {
              const radioGroup = form.getRadioGroup(fieldName);
              radioGroup.select(fieldName); // Select this specific button
            }
          } else if (fieldType === 'dropdown') {
            const dropdown = form.getDropdown(fieldName);
            if (Array.isArray(value)) {
              if (value.length > 0) {
                dropdown.select(value);
              }
            } else if (value && typeof value === 'string') {
              dropdown.select(value);
            }
          } else if (typeof value === 'string') {
            const textField = form.getTextField(fieldName);
            textField.setText(value);
          } else if (typeof value === 'boolean') {
            const checkBox = form.getCheckBox(fieldName);
            if (value) {
              checkBox.check();
            } else {
              checkBox.uncheck();
            }
          }
        } catch (err) {
          logger.warn(
            `[usePDFExport] Could not fill field with id ${fieldId}:`,
            err,
          );
        }
      });

      try {
        const formFields = form.getFields();
        if (formFields.length > 0) {
          if (shouldFlatten) {
            form.flatten();
            logger.info('[usePDFExport] Form fields flattened (non-editable)');
          } else {
            logger.debug('[usePDFExport] Form fields preserved (editable)');
          }
        } else {
          logger.debug('[usePDFExport] No form fields to flatten');
        }
      } catch (err) {
        logger.warn('[usePDFExport] Failed to flatten form fields:', err);
      }

      for (const image of images) {
        try {
          const page = pdfDoc.getPages()[image.pageNumber - 1];
          if (!page) {
            logger.warn(`[usePDFExport] Page ${image.pageNumber} not found`);
            // eslint-disable-next-line no-continue
            continue;
          }

          const imageResponse = await fetch(image.url);
          const imageArrayBuffer = await imageResponse.arrayBuffer();
          const imageBytes = new Uint8Array(imageArrayBuffer);

          let embeddedImage;
          if (
            image.url.includes('image/png') ||
            image.url.startsWith('data:image/png')
          ) {
            embeddedImage = await pdfDoc.embedPng(imageBytes);
          } else if (
            image.url.includes('image/jpeg') ||
            image.url.includes('image/jpg') ||
            image.url.startsWith('data:image/jpeg')
          ) {
            embeddedImage = await pdfDoc.embedJpg(imageBytes);
          } else {
            try {
              embeddedImage = await pdfDoc.embedPng(imageBytes);
            } catch {
              embeddedImage = await pdfDoc.embedJpg(imageBytes);
            }
          }

          const pageHeight = page.getHeight();
          const pdfCoords = screenToPDFCoords(
            image.position.x,
            image.position.y,
            pageHeight,
            image.size.height,
          );

          page.drawImage(embeddedImage, {
            x: pdfCoords.x,
            y: pdfCoords.y,
            width: image.size.width,
            height: image.size.height,
          });
        } catch (err) {
          logger.warn(`[usePDFExport] Could not embed image ${image.id}:`, err);
        }
      }

      const pdfBytes = await pdfDoc.save();

      setIsExporting(false);
      logger.info('[usePDFExport] Export finished');
      return pdfBytes;
    } catch (err) {
      logger.error('[usePDFExport] PDF export error:', err);
      setError(err instanceof Error ? err.message : 'Failed to export PDF');
      setIsExporting(false);
      throw err; // Re-throw for caller to handle
    }
  };

  return {
    exportPDF,
    isExporting,
    error,
  };
};
